import React from 'react';
import { PhoneIcon, CalendarIcon, MapPinIcon, ChevronDownIcon } from 'lucide-react';
import { motion } from 'framer-motion';
const CHAIR_IMG = 'https://cdn.magicpatterns.com/uploads/tJeWe6Uo8fYm4gRHVQhCcL/u8896349413_Cinematic_photo_of_a_vintage_barber_chair_in_a_mo_c4ba5aaf-e4f4-4e9e-a189-eaa260c04ed8_0.png';
export function Hero() {
  return <section className="relative w-full" aria-label="Hero section" data-id="element-86">
      {/* ══════════════════════════════════════════
              MOBILE HERO — full-bleed cinematic chair
              Hidden on md+ screens
          ══════════════════════════════════════════ */}
      <div className="relative md:hidden w-full" style={{
      minHeight: '100svh'
    }} data-id="element-87">
        {/* Background image */}
        <img src={CHAIR_IMG} alt="Vintage barber chair at Jonathan's Barbershop" className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-88" />

        {/* Multi-layer gradient for cinematic depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/30" aria-hidden="true" data-id="element-89" />

        {/* Content — vertically centered, not bottom-anchored */}
        <div className="relative z-20 flex flex-col justify-center min-h-[100svh] px-6 py-20" data-id="element-90">
          {/* Eyebrow */}
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-[#c41e3a] font-semibold tracking-[0.2em] uppercase text-xs mb-3" style={{
          fontFamily: 'Oswald, sans-serif'
        }} data-id="element-91">
            Hialeah's Most Trusted Barbershop
          </motion.p>

          {/* Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-[2.6rem] font-bold text-white leading-[1.0] mb-4" style={{
          fontFamily: 'Oswald, sans-serif',
          letterSpacing: '0.02em'
        }} data-id="element-92">
            YOUR NEXT
            <br data-id="element-93" />
            <span className="text-gradient-gold" data-id="element-94">FRESH CUT</span>
            <br data-id="element-95" />
            IS ONE CLICK AWAY
          </motion.h1>

          {/* Sub copy */}
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.38
        }} className="text-gray-300 text-sm leading-relaxed mb-4 max-w-xs" data-id="element-96">
            Precision cuts, clean fades, and real craftsmanship — by barbers who
            care.
          </motion.p>

          {/* Walk-ins badge */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.45
        }} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-xs text-white mb-5 self-start" data-id="element-97">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" aria-hidden="true" data-id="element-98" />
            Walk-Ins Welcome — Appointments Available
          </motion.div>

          {/* CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.55
        }} className="flex flex-col gap-3" data-id="element-99">
            <a href="#book" className="flex items-center justify-center gap-2 bg-[#c41e3a] text-white px-6 py-3.5 rounded font-bold text-sm shadow-[0_4px_24px_rgba(196,30,58,0.5)] w-full" aria-label="Book your appointment" style={{
            fontFamily: 'Oswald, sans-serif',
            letterSpacing: '0.1em'
          }} data-id="element-100">
              <CalendarIcon size={16} data-id="element-101" />
              BOOK YOUR APPOINTMENT
            </a>
            <a href="tel:7864095863" className="flex items-center justify-center gap-2 bg-black/40 border border-white/25 text-white px-6 py-3.5 rounded font-bold text-sm w-full backdrop-blur-sm" aria-label="Call us at (786) 409-5863" style={{
            fontFamily: 'Oswald, sans-serif',
            letterSpacing: '0.1em'
          }} data-id="element-102">
              <PhoneIcon size={16} data-id="element-103" />
              CALL (786) 409-5863
            </a>
          </motion.div>

          {/* Address */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.7
        }} className="flex items-center gap-2 mt-4 text-gray-400 text-xs" data-id="element-104">
            <MapPinIcon size={11} className="text-[#c41e3a] flex-shrink-0" data-id="element-105" />
            <span data-id="element-106">2851 W 68 St Suite 11, Hialeah Gardens, FL 33018</span>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.4,
        duration: 0.4
      }} className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 text-gray-500" aria-hidden="true" data-id="element-107">
          <motion.div animate={{
          y: [0, 5, 0]
        }} transition={{
          repeat: Infinity,
          duration: 1.8,
          ease: 'easeInOut'
        }} data-id="element-108">
            <ChevronDownIcon size={20} data-id="element-109" />
          </motion.div>
        </motion.div>
      </div>

      {/* ══════════════════════════════════════════
              DESKTOP HERO — original banner layout
              Hidden on mobile
          ══════════════════════════════════════════ */}
      <div className="hidden md:block" data-id="element-110">
        {/* ── BANNER ── */}
        <div className="relative w-full" data-id="element-111">
          <img src="https://cdn.magicpatterns.com/uploads/nmEDqMPmJbg35DCUTXgV7V/fcb.png" alt="Jonathan's Barbershop — Fresh Cuts, Clean Fades" width={1400} height={525} fetchPriority="high" decoding="async" className="w-full h-auto object-cover block" style={{
          aspectRatio: '1400/525'
        }} data-id="element-112" />
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0d0d0d] to-transparent" aria-hidden="true" data-id="element-113" />

          {/* Barber pole — left */}
          <div className="absolute left-0 top-0 bottom-0 w-7 overflow-hidden" aria-hidden="true" data-id="element-114">
            <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-gray-300 to-gray-500 z-10 rounded-b-sm shadow-md" data-id="element-115" />
            <div className="absolute inset-0 top-3 bottom-3 barber-pole-banner" data-id="element-116" />
            <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-t from-gray-300 to-gray-500 z-10 rounded-t-sm shadow-md" data-id="element-117" />
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-black/30 z-20 pointer-events-none" data-id="element-118" />
          </div>

          {/* Barber pole — right */}
          <div className="absolute right-0 top-0 bottom-0 w-7 overflow-hidden" aria-hidden="true" data-id="element-119">
            <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-gray-300 to-gray-500 z-10 rounded-b-sm shadow-md" data-id="element-120" />
            <div className="absolute inset-0 top-3 bottom-3 barber-pole-banner barber-pole-banner-reverse" data-id="element-121" />
            <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-t from-gray-300 to-gray-500 z-10 rounded-t-sm shadow-md" data-id="element-122" />
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-black/30 z-20 pointer-events-none" data-id="element-123" />
          </div>
        </div>

        {/* ── HERO CONTENT ── */}
        <div className="w-full bg-[#0d0d0d] px-4 sm:px-8 pb-16 pt-2" data-id="element-124">
          <div className="max-w-5xl mx-auto text-center" data-id="element-125">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex justify-center mb-6 -mt-8 sm:-mt-10 relative z-10" data-id="element-126" />

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-[#c41e3a] font-semibold tracking-[0.25em] uppercase text-sm md:text-base mb-4" style={{
            fontFamily: 'Oswald, sans-serif'
          }} data-id="element-127">
              Hialeah's Most Trusted Barbershop
            </motion.p>

            <motion.h1 initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-6" style={{
            fontFamily: 'Oswald, sans-serif',
            letterSpacing: '0.02em'
          }} data-id="element-128">
              YOUR NEXT
              <br data-id="element-129" />
              <span className="text-gradient-gold" data-id="element-130">FRESH CUT</span>
              <br data-id="element-131" />
              IS ONE CLICK AWAY
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="text-gray-300 text-base md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed" data-id="element-132">
              Precision cuts, clean fades, and real craftsmanship — by barbers
              who actually care about how you look when you walk out that door.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.5
          }} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm text-white mb-8" data-id="element-133">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" aria-hidden="true" data-id="element-134" />
              Walk-Ins Welcome — Appointments Available
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.6
          }} className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-135">
              <a href="#book" className="flex items-center gap-3 bg-[#c41e3a] hover:bg-[#a01830] text-white px-8 py-4 rounded font-bold text-lg transition-colors duration-200 shadow-[0_4px_20px_rgba(196,30,58,0.4)] w-full sm:w-auto justify-center" aria-label="Book your appointment" style={{
              fontFamily: 'Oswald, sans-serif',
              letterSpacing: '0.1em'
            }} data-id="element-136">
                <CalendarIcon size={20} data-id="element-137" />
                BOOK YOUR APPOINTMENT
              </a>
              <a href="tel:7864095863" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded font-bold text-lg transition-colors duration-200 w-full sm:w-auto justify-center" aria-label="Call us at (786) 409-5863" style={{
              fontFamily: 'Oswald, sans-serif',
              letterSpacing: '0.1em'
            }} data-id="element-138">
                <PhoneIcon size={20} data-id="element-139" />
                CALL (786) 409-5863
              </a>
            </motion.div>

            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.75
          }} className="flex items-center justify-center gap-2 mt-6 text-gray-400 text-sm" data-id="element-140">
              <MapPinIcon size={14} className="text-[#c41e3a] flex-shrink-0" data-id="element-141" />
              <span data-id="element-142">2851 W 68 St Suite 11, Hialeah Gardens, FL 33018</span>
            </motion.div>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2,
        duration: 0.4
      }} className="flex justify-center pb-6 bg-[#0d0d0d] text-gray-600" aria-hidden="true" data-id="element-143">
          <motion.div animate={{
          y: [0, 6, 0]
        }} transition={{
          repeat: Infinity,
          duration: 1.8,
          ease: 'easeInOut'
        }} data-id="element-144">
            <ChevronDownIcon size={24} data-id="element-145" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
}